import { Routes } from '@angular/router';

import { ProfileComponent } from './pages/profile/profile.component';
import { FormElementsComponent } from './pages/forms/form-elements/form-elements.component';
import { BasicTablesComponent } from './pages/tables/basic-tables/basic-tables.component';
import { BlankComponent } from './pages/blank/blank.component';
import { NotFoundComponent } from './pages/other-page/not-found/not-found.component';
import { ForbiddenComponent } from './pages/forbidden/forbidden.component';
import { AppLayoutComponent } from './shared/layout/app-layout/app-layout.component';
import { InvoicesListComponent } from './pages/invoices/invoices-list/invoices-list.component';
import { InvoiceDetailComponent } from './pages/invoices/invoice-detail/invoice-detail.component';
import { TransactionsListComponent } from './pages/transactions/transactions-list/transactions-list.component';
import { TransactionDetailComponent } from './pages/transactions/transaction-detail/transaction-detail.component';
import { PaymentMethodsListComponent } from './pages/payment-methods/payment-methods-list/payment-methods-list.component';
import { PaymentMethodCreateComponent } from './pages/payment-methods/payment-method-create/payment-method-create.component';
import { PaymentMethodDetailComponent } from './pages/payment-methods/payment-method-detail/payment-method-detail.component';
import { PaymentMethodEditComponent } from './pages/payment-methods/payment-method-edit/payment-method-edit.component';
import { WalletsListComponent } from './pages/wallets/wallets-list/wallets-list.component';
import { WalletDetailComponent } from './pages/wallets/wallet-detail/wallet-detail.component';
import { OperatorsListComponent } from './pages/operators/operators-list/operators-list.component';
import { OperatorDetailComponent } from './pages/operators/operator-detail/operator-detail.component';
import { OperatorCreateComponent } from './pages/operators/operator-create/operator-create.component';
import { OperatorEditComponent } from './pages/operators/operator-edit/operator-edit.component';
import { KycQueueComponent } from './pages/kyc/kyc-queue/kyc-queue.component';
import { LicensePricingComponent } from './pages/licenses/license-pricing/license-pricing.component';
import { ZonesListComponent } from './pages/zones/zones-list/zones-list.component';
import { CommissionConfigComponent } from './pages/commissions/commission-config/commission-config.component';
import { LineChartComponent } from './pages/charts/line-chart/line-chart.component';
import { BarChartComponent } from './pages/charts/bar-chart/bar-chart.component';
import { AlertsComponent } from './pages/ui-elements/alerts/alerts.component';
import { AvatarElementComponent } from './pages/ui-elements/avatar-element/avatar-element.component';
import { BadgesComponent } from './pages/ui-elements/badges/badges.component';
import { ButtonsComponent } from './pages/ui-elements/buttons/buttons.component';
import { ImagesComponent } from './pages/ui-elements/images/images.component';
import { VideosComponent } from './pages/ui-elements/videos/videos.component';
import { SignInComponent } from './pages/auth-pages/sign-in/sign-in.component';
import { SignUpComponent } from './pages/auth-pages/sign-up/sign-up.component';
import { CalenderComponent } from './pages/calender/calender.component';
import { UsersListComponent } from './pages/users/users-list/users-list.component';
import { UserCreateComponent } from './pages/users/user-create/user-create.component';
import { UserDetailComponent } from './pages/users/user-detail/user-detail.component';
import { UserEditComponent } from './pages/users/user-edit/user-edit.component';
import { RolesListComponent } from './pages/roles/roles-list/roles-list.component';
import { RoleCreateComponent } from './pages/roles/role-create/role-create.component';
import { RoleDetailComponent } from './pages/roles/role-detail/role-detail.component';
import { RoleEditComponent } from './pages/roles/role-edit/role-edit.component';
import { PermissionsListComponent } from './pages/permissions/permissions-list/permissions-list.component';
import { PermissionCreateComponent } from './pages/permissions/permission-create/permission-create.component';
import { PermissionDetailComponent } from './pages/permissions/permission-detail/permission-detail.component';
import { ResourcesListComponent } from './pages/permissions/resources-list/resources-list.component';
import { ResourceCreateComponent } from './pages/permissions/resource-create/resource-create.component';
import { ResourceDetailComponent } from './pages/permissions/resource-detail/resource-detail.component';
import { ResourceEditComponent } from './pages/permissions/resource-edit/resource-edit.component';
import { ScopesListComponent } from './pages/permissions/scopes-list/scopes-list.component';
import { RoleAssignmentsComponent } from './pages/role-assignments/role-assignments.component';
import { OwnersListComponent } from './pages/owners/owners-list/owners-list.component';
import { OwnerDetailComponent } from './pages/owners/owner-detail/owner-detail.component';
import { VehiclesListComponent } from './pages/vehicles/vehicles-list/vehicles-list.component';
import { VehicleDetailComponent } from './pages/vehicles/vehicle-detail/vehicle-detail.component';
import { OptionsListComponent } from './pages/options/options-list/options-list.component';
import { OptionCreateComponent } from './pages/options/option-create/option-create.component';
import { OptionDetailComponent } from './pages/options/option-detail/option-detail.component';
import { OptionEditComponent } from './pages/options/option-edit/option-edit.component';
import { RentalsListComponent } from './pages/rentals/rentals-list/rentals-list.component';
import { RentalDetailComponent } from './pages/rentals/rental-detail/rental-detail.component';

import { roleGuard } from './shared/guards/role.guard';

/**
 * Admin app : every route under AppLayoutComponent requires the ADMIN role.
 * Public routes : /signin, /signup (kept during scaffolding), /forbidden, and the catch-all 404.
 */
export const routes: Routes = [
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [roleGuard('ADMIN')],
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/dashboard/admin-dashboard/admin-dashboard.component').then(
            (m) => m.AdminDashboardComponent,
          ),
        title: 'Nawam Admin — Tableau de bord',
      },
      // ── Utilisateurs & accès ───────────────────────────────────────────
      // NOTE: `users/new` doit précéder `users/:id` sinon "new" est capté comme un id.
      { path: 'users', component: UsersListComponent, title: 'Nawam Admin — Utilisateurs' },
      { path: 'users/new', component: UserCreateComponent, title: 'Nawam Admin — Nouvel utilisateur' },
      { path: 'users/:id', component: UserDetailComponent, title: 'Nawam Admin — Fiche utilisateur' },
      { path: 'users/:id/edit', component: UserEditComponent, title: 'Nawam Admin — Modifier l\'utilisateur' },
      // Rôles : `roles/new` avant `roles/:name` pour ne pas capter "new" comme nom.
      { path: 'roles', component: RolesListComponent, title: 'Nawam Admin — Rôles' },
      { path: 'roles/new', component: RoleCreateComponent, title: 'Nawam Admin — Nouveau rôle' },
      { path: 'roles/:name', component: RoleDetailComponent, title: 'Nawam Admin — Fiche rôle' },
      { path: 'roles/:name/edit', component: RoleEditComponent, title: 'Nawam Admin — Modifier le rôle' },
      // Permissions : paths statiques (`new`, `resources`, `scopes`) AVANT `:id` pour éviter la capture.
      { path: 'permissions', component: PermissionsListComponent, title: 'Nawam Admin — Permissions' },
      { path: 'permissions/new', component: PermissionCreateComponent, title: 'Nawam Admin — Nouvelle permission' },
      { path: 'permissions/resources', component: ResourcesListComponent, title: 'Nawam Admin — Resources' },
      { path: 'permissions/resources/new', component: ResourceCreateComponent, title: 'Nawam Admin — Nouvelle resource' },
      { path: 'permissions/resources/:id', component: ResourceDetailComponent, title: 'Nawam Admin — Fiche resource' },
      { path: 'permissions/resources/:id/edit', component: ResourceEditComponent, title: 'Nawam Admin — Modifier la resource' },
      { path: 'permissions/scopes', component: ScopesListComponent, title: 'Nawam Admin — Scopes' },
      { path: 'permissions/:id', component: PermissionDetailComponent, title: 'Nawam Admin — Fiche permission' },
      // Page standalone d'assignation de rôles aux utilisateurs.
      { path: 'role-assignments', component: RoleAssignmentsComponent, title: 'Nawam Admin — Assignations' },
      // ── Plateforme — Propriétaires ─────────────────────────────────────
      { path: 'owners', component: OwnersListComponent, title: 'Nawam Admin — Propriétaires' },
      { path: 'owners/:id', component: OwnerDetailComponent, title: 'Nawam Admin — Fiche propriétaire' },
      // ── Plateforme — Liste d'attente chauffeurs / passagers (site client) ──
      {
        path: 'driver-leads',
        loadComponent: () => import('./pages/driver-leads/driver-leads-list.component').then((m) => m.DriverLeadsListComponent),
        title: 'Nawam Admin — Liste d’attente chauffeurs',
      },
      // ── Plateforme — Clients (vérification permis) ─────────────────────
      {
        path: 'customers',
        loadComponent: () =>
          import('./pages/customers/customers-list/customers-list.component').then(
            (m) => m.CustomersListComponent,
          ),
        title: 'Nawam Admin — Clients',
      },
      {
        path: 'customers/:id',
        loadComponent: () =>
          import('./pages/customers/customer-detail/customer-detail.component').then(
            (m) => m.CustomerDetailComponent,
          ),
        title: 'Nawam Admin — Fiche client',
      },
      // ── Plateforme — Véhicules ─────────────────────────────────────────
      { path: 'vehicles', component: VehiclesListComponent, title: 'Nawam Admin — Véhicules' },
      { path: 'vehicles/:id', component: VehicleDetailComponent, title: 'Nawam Admin — Fiche véhicule' },
      // ── Plateforme — Équipements (référentiel options véhicule) ────────
      // NOTE: `options/new` doit précéder `options/:id`.
      { path: 'options', component: OptionsListComponent, title: 'Nawam Admin — Équipements' },
      { path: 'options/new', component: OptionCreateComponent, title: 'Nawam Admin — Nouvel équipement' },
      { path: 'options/:id', component: OptionDetailComponent, title: 'Nawam Admin — Fiche équipement' },
      { path: 'options/:id/edit', component: OptionEditComponent, title: 'Nawam Admin — Modifier l\'équipement' },
      // ── Plateforme — Locations ─────────────────────────────────────────
      { path: 'rentals', component: RentalsListComponent, title: 'Nawam Admin — Locations' },
      { path: 'rentals/:id', component: RentalDetailComponent, title: 'Nawam Admin — Fiche location' },
      // ── Plateforme — Factures ──────────────────────────────────────────
      { path: 'invoices', component: InvoicesListComponent, title: 'Nawam Admin — Factures' },
      { path: 'invoices/:id', component: InvoiceDetailComponent, title: 'Nawam Admin — Fiche facture' },
      // ── Plateforme — Transactions cross-service ────────────────────────
      { path: 'transactions', component: TransactionsListComponent, title: 'Nawam Admin — Transactions' },
      { path: 'transactions/:id', component: TransactionDetailComponent, title: 'Nawam Admin — Fiche transaction' },
      // ── Plateforme — Méthodes de paiement (config admin) ───────────────
      // NOTE: `payment-methods/new` doit précéder `payment-methods/:id`.
      { path: 'payment-methods', component: PaymentMethodsListComponent, title: 'Nawam Admin — Méthodes de paiement' },
      { path: 'payment-methods/new', component: PaymentMethodCreateComponent, title: 'Nawam Admin — Nouvelle méthode' },
      { path: 'payment-methods/:id', component: PaymentMethodDetailComponent, title: 'Nawam Admin — Fiche méthode' },
      { path: 'payment-methods/:id/edit', component: PaymentMethodEditComponent, title: 'Nawam Admin — Modifier la méthode' },
      // ── Plateforme — Wallets (supervision financière) ──────────────────
      { path: 'wallets', component: WalletsListComponent, title: 'Nawam Admin — Wallets' },
      { path: 'wallets/:ownerType/:ownerRef', component: WalletDetailComponent, title: 'Nawam Admin — Snapshot wallet' },
      // ── Plateforme — Opérateurs (B2B) ──────────────────────────────────
      // NOTE: `operators/new` doit précéder `operators/:id`.
      { path: 'operators', component: OperatorsListComponent, title: 'Nawam Admin — Opérateurs' },
      { path: 'operators/new', component: OperatorCreateComponent, title: 'Nawam Admin — Nouvel opérateur' },
      { path: 'operators/:id', component: OperatorDetailComponent, title: 'Nawam Admin — Fiche opérateur' },
      { path: 'operators/:id/edit', component: OperatorEditComponent, title: 'Nawam Admin — Modifier l\'opérateur' },
      // ── Plateforme — Demandes de changement d'opérateur (#36, audit) ────
      {
        path: 'change-requests',
        loadComponent: () =>
          import('./pages/change-requests/change-requests-list/change-requests-list.component').then(
            (m) => m.ChangeRequestsListComponent,
          ),
        title: 'Nawam Admin — Demandes de changement',
      },
      // ── KYC — parc documentaire tous acteurs, filtrable par statut (dont
      //    EXPIRED). Le dossier d'un opérateur : /operators/{id}#kyc ──
      { path: 'kyc', component: KycQueueComponent, title: 'Nawam Admin — Pièces KYC' },
      // ── Opérateurs — dossiers KYC à valider (#66) ──────────────────────
      {
        path: 'operator-kyc',
        loadComponent: () =>
          import('./pages/operators/operator-kyc-queue/operator-kyc-queue.component').then(
            (m) => m.OperatorKycQueueComponent,
          ),
        title: 'Nawam Admin — Dossiers KYC opérateur',
      },
      // ── Plateforme — Licences (tarification admin) ─────────────────────
      { path: 'license-pricing', component: LicensePricingComponent, title: 'Nawam Admin — Tarification licences' },
      // ── Plateforme — Zones géographiques ───────────────────────────────
      { path: 'zones', component: ZonesListComponent, title: 'Nawam Admin — Zones' },
      // ── Plateforme — Commission globale ────────────────────────────────
      { path: 'commission-config', component: CommissionConfigComponent, title: 'Nawam Admin — Commission globale' },
      // ── Plateforme — Politiques d'annulation & frais de retard ─────────
      {
        path: 'cancellation-policies',
        loadComponent: () =>
          import('./pages/policies/cancellation-policies-list/cancellation-policies-list.component').then(
            (m) => m.CancellationPoliciesListComponent,
          ),
        title: "Nawam Admin — Politiques d'annulation",
      },
      {
        path: 'late-fee-policies',
        loadComponent: () =>
          import('./pages/policies/late-fee-policies-list/late-fee-policies-list.component').then(
            (m) => m.LateFeePoliciesListComponent,
          ),
        title: 'Nawam Admin — Frais de retard',
      },
      // ── Plateforme — Caution (#46) ─────────────────────────────────────
      {
        path: 'deposit-policy',
        loadComponent: () =>
          import('./pages/deposit-policy/deposit-policy.component').then(
            (m) => m.DepositPolicyComponent,
          ),
        title: 'Nawam Admin — Caution',
      },
      // ── Plateforme — Dettes prestataires (#49) ─────────────────────────
      {
        path: 'debts',
        loadComponent: () =>
          import('./pages/debts/debts-list/debts-list.component').then(
            (m) => m.DebtsListComponent,
          ),
        title: 'Nawam Admin — Dettes prestataires',
      },
      // ── Plateforme — Retraits BALANCE à valider (#57) ──────────────────
      {
        path: 'withdrawals',
        loadComponent: () =>
          import('./pages/withdrawals/withdrawals-list/withdrawals-list.component').then(
            (m) => m.WithdrawalsListComponent,
          ),
        title: 'Nawam Admin — Retraits à valider',
      },
      // ── Plateforme — Réconciliation hebdomadaire (#47/#49) ─────────────
      {
        path: 'cash-reconciliation',
        loadComponent: () =>
          import('./pages/cash-reconciliation/cash-reconciliation-list/cash-reconciliation-list.component').then(
            (m) => m.CashReconciliationListComponent,
          ),
        title: 'Nawam Admin — Réconciliation hebdomadaire',
      },
      // ── Plateforme — Trésorerie plateforme (#60) ───────────────────────
      {
        path: 'treasury',
        loadComponent: () =>
          import('./pages/treasury/treasury-dashboard/treasury-dashboard.component').then(
            (m) => m.TreasuryDashboardComponent,
          ),
        title: 'Nawam Admin — Trésorerie plateforme',
      },
      // ── Plateforme — Notifications (centre de notifs) ──────────────────
      {
        path: 'notifications',
        loadComponent: () =>
          import('./pages/notifications/notifications-list/notifications-list.component').then(
            (m) => m.NotificationsListComponent,
          ),
        title: 'Nawam Admin — Notifications',
      },
      // ── Démo template (à retirer en Phase 4) ───────────────────────────
      { path: 'calendar', component: CalenderComponent, title: 'Nawam Admin — Calendrier' },
      { path: 'profile', component: ProfileComponent, title: 'Nawam Admin — Profil' },
      { path: 'form-elements', component: FormElementsComponent, title: 'Nawam Admin — Formulaires' },
      { path: 'basic-tables', component: BasicTablesComponent, title: 'Nawam Admin — Tables' },
      { path: 'blank', component: BlankComponent, title: 'Nawam Admin — Page vierge' },
      { path: 'line-chart', component: LineChartComponent, title: 'Nawam Admin — Graphique ligne' },
      { path: 'bar-chart', component: BarChartComponent, title: 'Nawam Admin — Graphique barres' },
      { path: 'alerts', component: AlertsComponent, title: 'Nawam Admin — Alertes' },
      { path: 'avatars', component: AvatarElementComponent, title: 'Nawam Admin — Avatars' },
      { path: 'badge', component: BadgesComponent, title: 'Nawam Admin — Badges' },
      { path: 'buttons', component: ButtonsComponent, title: 'Nawam Admin — Boutons' },
      { path: 'images', component: ImagesComponent, title: 'Nawam Admin — Images' },
      { path: 'videos', component: VideosComponent, title: 'Nawam Admin — Vidéos' },
    ],
  },
  // ── public ────────────────────────────────────────────────────────────────
  { path: 'signin', component: SignInComponent, title: 'Nawam Admin — Connexion' },
  { path: 'signup', component: SignUpComponent, title: 'Nawam Admin — Inscription' },
  {
    path: 'auth/set-password/:token',
    loadComponent: () =>
      import('./pages/auth-pages/set-password/set-password.component').then(
        (m) => m.SetPasswordComponent,
      ),
    title: 'Nawam Admin — Définir mon mot de passe',
  },
  {
    path: 'forgotten-password',
    loadComponent: () =>
      import('./pages/auth-pages/forgotten-password/forgotten-password.component').then(
        (m) => m.ForgottenPasswordComponent,
      ),
    title: 'Nawam Admin — Mot de passe oublié',
  },
  { path: 'forbidden', component: ForbiddenComponent, title: 'Nawam Admin — Accès refusé' },
  // ── catch-all ────────────────────────────────────────────────────────────
  { path: '**', component: NotFoundComponent, title: 'Nawam Admin — Page introuvable' },
];
